<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import AgencyProfileAPI from 'dashboard/api/agencyProfile';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisToggle from 'dashboard/components-next/LaisToggle.vue';
import {
  AGENCY_ORIGINS,
  DEFAULT_AGENCY_ORIGINS,
  normalizeAgencyOrigins,
} from 'dashboard/helper/lais/agencyOrigins';

const UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const emptyAddress = () => ({
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
});

const emptyBranding = () => ({
  primary_color: '',
  secondary_color: '',
  accent_color: '',
  dark_mode_default: false,
});

const emptyBranch = () => ({
  name: '',
  code: '',
  branch_kind: 'branch',
  status: 'active',
  public_visibility: true,
  address: {
    bairro: '',
    cidade: '',
    uf: '',
  },
  public_contact: {
    phone: '',
    email: '',
  },
});

const emptyLicense = () => ({
  holder_kind: 'agency',
  license_kind: 'creci_j',
  number: '',
  uf: '',
  status: 'active',
  valid_until: '',
  agency_branch_id: '',
  capabilities: ['publish_listing'],
});

const emptyTerritory = () => ({
  name: '',
  modality: 'sale',
  property_kind: 'apartment',
  channel: 'any',
  agency_branch_id: '',
  coverage_kind: 'city',
  coverage_uf: '',
  coverage_city: '',
  coverage_neighborhoods: '',
});

const form = reactive({
  nome_fantasia: '',
  razao_social: '',
  cnpj: '',
  creci: '',
  creci_uf: '',
  logo_url: '',
  site_url: '',
  telefone: '',
  email_contato: '',
  endereco: emptyAddress(),
  origens_default: [...DEFAULT_AGENCY_ORIGINS],
  branding: emptyBranding(),
  white_label_enabled: false,
  apply_to_account_origins: false,
});

const branchDraft = reactive(emptyBranch());
const licenseDraft = reactive(emptyLicense());
const territoryDraft = reactive(emptyTerritory());
const isLoading = ref(true);
const isSaving = ref(false);
const operationLoading = ref('');
const loadError = ref('');
const validationErrors = ref([]);
const fieldErrors = ref({});
const canUpdate = ref(false);
const persisted = ref(false);
const activeContext = ref({});
const impactSummary = ref({});
const users = ref([]);
const corretores = ref([]);
const branches = ref([]);
const licenses = ref([]);
const territories = ref([]);
const sensitiveActions = ref([]);
const originOptions = ref(AGENCY_ORIGINS);
const logoAttachmentUrl = ref('');
const logoPreviewUrl = ref('');
const logoFile = ref(null);
const removeLogo = ref(false);

const readOnly = computed(() => !canUpdate.value);
const selectedOriginLabels = computed(() =>
  originOptions.value
    .filter(origin => form.origens_default.includes(origin.value))
    .map(origin => origin.label)
);
const previewBranding = computed(() => ({
  primary: form.branding.primary_color || '#49317a',
  secondary: form.branding.secondary_color || '#28E4A8',
  accent: form.branding.accent_color || '#e9b76f',
}));
const logoImageUrl = computed(
  () => logoPreviewUrl.value || logoAttachmentUrl.value || form.logo_url
);
const availableAccounts = computed(
  () => activeContext.value?.available_accounts || []
);
const switchableAccounts = computed(() =>
  availableAccounts.value.filter(account => !account.current)
);
const branchOptions = computed(() =>
  branches.value.map(branch => ({
    id: branch.id,
    label: `${branch.name} (${branch.code})`,
  }))
);
const blockedSensitiveActions = computed(() =>
  sensitiveActions.value.filter(action => action.gate?.blocked)
);
const isBranchSaving = computed(() => operationLoading.value === 'branch');
const isLicenseSaving = computed(() => operationLoading.value === 'license');
const isTerritorySaving = computed(
  () => operationLoading.value === 'territory'
);

const inputClass = computed(() => [
  'w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] text-lais-text outline-none transition focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/15',
  readOnly.value ? 'cursor-not-allowed bg-slate-50 text-lais-text-muted' : '',
]);

const assignPayload = payload => {
  form.nome_fantasia = payload.nome_fantasia || '';
  form.razao_social = payload.razao_social || '';
  form.cnpj = payload.cnpj || '';
  form.creci = payload.creci || '';
  form.creci_uf = payload.creci_uf || '';
  form.logo_url = payload.logo_url || '';
  form.site_url = payload.site_url || '';
  form.telefone = payload.telefone || '';
  form.email_contato = payload.email_contato || '';
  form.endereco = { ...emptyAddress(), ...(payload.endereco || {}) };
  form.branding = { ...emptyBranding(), ...(payload.branding || {}) };
  form.white_label_enabled = Boolean(payload.white_label_enabled);
  form.origens_default = normalizeAgencyOrigins(payload.origens_default);
  form.apply_to_account_origins = false;
  logoAttachmentUrl.value = payload.logo_attachment_url || '';
  logoPreviewUrl.value = '';
  logoFile.value = null;
  removeLogo.value = false;

  persisted.value = Boolean(payload.persisted);
  canUpdate.value = Boolean(payload.can_update);
  originOptions.value = payload.origin_options?.length
    ? payload.origin_options
    : AGENCY_ORIGINS;
  activeContext.value = payload.active_context || {};
  impactSummary.value = payload.impact_summary || {};
  users.value = payload.users || [];
  corretores.value = payload.corretores || [];
  branches.value = payload.branches || [];
  licenses.value = payload.licenses || [];
  territories.value = payload.territories || [];
  sensitiveActions.value = payload.sensitive_actions || [];
};

const resetValidationState = () => {
  validationErrors.value = [];
  fieldErrors.value = {};
};

const fieldError = key => {
  const value = fieldErrors.value[key];
  if (!value) return '';
  return Array.isArray(value) ? value.join(', ') : String(value);
};

const normalizeFieldErrors = rawErrors =>
  Object.entries(rawErrors || {}).reduce((acc, [key, value]) => {
    acc[key] = Array.isArray(value) ? value : [value];
    return acc;
  }, {});

const applyValidationErrors = error => {
  const responseErrors = error?.response?.data?.errors;
  const responseFieldErrors = error?.response?.data?.field_errors;
  fieldErrors.value = normalizeFieldErrors(responseFieldErrors);

  if (Array.isArray(responseErrors)) {
    validationErrors.value = responseErrors.filter(errorMessage => {
      const fieldMessages = Object.values(fieldErrors.value).flat();
      return !fieldMessages.includes(errorMessage);
    });
    return;
  }

  validationErrors.value = [];
};

const errorMessage = error => {
  applyValidationErrors(error);

  return (
    validationErrors.value[0] ||
    error?.response?.data?.error ||
    error?.message ||
    'Não foi possível salvar o perfil da imobiliária.'
  );
};

const loadProfile = async () => {
  isLoading.value = true;
  loadError.value = '';
  resetValidationState();

  try {
    const { data } = await AgencyProfileAPI.get();
    assignPayload(data || {});
  } catch (error) {
    loadError.value =
      error?.response?.data?.error ||
      error?.message ||
      'Não foi possível carregar o perfil da imobiliária.';
  } finally {
    isLoading.value = false;
  }
};

const buildPayload = () => ({
  nome_fantasia: form.nome_fantasia,
  razao_social: form.razao_social,
  cnpj: form.cnpj,
  creci: form.creci,
  creci_uf: form.creci_uf,
  logo_url: form.logo_url,
  site_url: form.site_url,
  telefone: form.telefone,
  email_contato: form.email_contato,
  endereco: { ...form.endereco },
  origens_default: [...form.origens_default],
  branding: { ...form.branding },
  white_label_enabled: form.white_label_enabled,
  apply_to_account_origins: form.apply_to_account_origins,
  logo: logoFile.value,
  remove_logo: removeLogo.value,
});

const onLogoUpload = ({ file, url }) => {
  logoFile.value = file;
  logoPreviewUrl.value = url || '';
  removeLogo.value = false;
};

const onLogoDelete = () => {
  logoFile.value = null;
  logoPreviewUrl.value = '';
  removeLogo.value = Boolean(logoAttachmentUrl.value);
  logoAttachmentUrl.value = '';
};

const saveProfile = async () => {
  if (readOnly.value) return;

  isSaving.value = true;
  resetValidationState();

  try {
    const { data } = await AgencyProfileAPI.update(buildPayload());
    assignPayload(data || {});
    useAlert('Perfil da imobiliária salvo.');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const resetBranchDraft = () => {
  Object.assign(branchDraft, emptyBranch());
};

const resetLicenseDraft = () => {
  Object.assign(licenseDraft, emptyLicense());
};

const resetTerritoryDraft = () => {
  Object.assign(territoryDraft, emptyTerritory());
};

const operationalErrorMessage = error => {
  applyValidationErrors(error);
  return (
    validationErrors.value[0] ||
    error?.response?.data?.errors?.[0] ||
    error?.response?.data?.error ||
    error?.message ||
    'Não foi possível salvar a governança da imobiliária.'
  );
};

const saveBranch = async () => {
  if (readOnly.value) return;

  operationLoading.value = 'branch';
  resetValidationState();

  try {
    const { data } = await AgencyProfileAPI.createBranch({
      ...branchDraft,
      address: { ...branchDraft.address },
      public_contact: { ...branchDraft.public_contact },
    });
    assignPayload(data || {});
    resetBranchDraft();
    useAlert('Filial salva.');
  } catch (error) {
    useAlert(operationalErrorMessage(error));
  } finally {
    operationLoading.value = '';
  }
};

const saveLicense = async () => {
  if (readOnly.value) return;

  operationLoading.value = 'license';
  resetValidationState();

  try {
    const payload = {
      ...licenseDraft,
      agency_branch_id:
        licenseDraft.holder_kind === 'branch'
          ? licenseDraft.agency_branch_id
          : undefined,
    };
    const { data } = await AgencyProfileAPI.createLicense(payload);
    assignPayload(data || {});
    resetLicenseDraft();
    useAlert('Licença salva.');
  } catch (error) {
    useAlert(operationalErrorMessage(error));
  } finally {
    operationLoading.value = '';
  }
};

const territoryCoveragePayload = () => {
  if (territoryDraft.coverage_kind === 'neighborhood') {
    return {
      uf: territoryDraft.coverage_uf,
      city: territoryDraft.coverage_city,
      neighborhoods: territoryDraft.coverage_neighborhoods
        .split(',')
        .map(item => item.trim())
        .filter(Boolean),
    };
  }

  if (territoryDraft.coverage_kind === 'uf') {
    return { uf: territoryDraft.coverage_uf };
  }

  return {
    uf: territoryDraft.coverage_uf,
    city: territoryDraft.coverage_city,
  };
};

const saveTerritory = async () => {
  if (readOnly.value) return;

  operationLoading.value = 'territory';
  resetValidationState();

  try {
    const { data } = await AgencyProfileAPI.createTerritory({
      name: territoryDraft.name,
      modality: territoryDraft.modality,
      property_kind: territoryDraft.property_kind,
      channel: territoryDraft.channel,
      agency_branch_id: territoryDraft.agency_branch_id || undefined,
      version: {
        coverage_kind: territoryDraft.coverage_kind,
        coverage_payload: territoryCoveragePayload(),
        valid_from: new Date().toISOString(),
      },
      assignments: [],
    });
    assignPayload(data || {});
    resetTerritoryDraft();
    useAlert('Território publicado.');
  } catch (error) {
    useAlert(operationalErrorMessage(error));
  } finally {
    operationLoading.value = '';
  }
};

const switchContext = async account => {
  try {
    const { data } = await AgencyProfileAPI.switchContext({
      target_account_id: account.account_id,
      reason: 'account_switch',
      idempotency_key: `agency-context-${Date.now()}-${account.account_id}`,
    });
    useAlert('Troca de contexto registrada.');
    if (data?.redirect_url) window.location.assign(data.redirect_url);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        'Não foi possível trocar o contexto ativo.'
    );
  }
};

const formatDate = value => {
  if (!value) return 'Sem vencimento';
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(
    new Date(value)
  );
};

const licenseStatusLabel = license => {
  if (license.active) return 'Ativa';
  if (license.status === 'expired') return 'Vencida';
  return license.status || 'Pendente';
};

const licenseStatusClass = license =>
  license.active
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : 'bg-rose-50 text-rose-700 ring-rose-200';

onMounted(loadProfile);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Perfil da imobiliária"
      helper="Identidade, marca, contato e origens padrão da conta Imudei."
      title-badge="Admin"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="isLoading"
          @click="loadProfile"
        >
          Atualizar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-save"
          :is-loading="isSaving"
          :disabled="readOnly || isLoading"
          @click="saveProfile"
        >
          Salvar perfil
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        v-if="isLoading"
        class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]"
      >
        <div class="h-80 animate-pulse rounded-lais bg-white" />
        <div class="h-80 animate-pulse rounded-lais bg-white" />
      </div>

      <LaisCard v-else-if="loadError" title="Não foi possível carregar">
        <p class="text-[13px] leading-6 text-rose-700">{{ loadError }}</p>
        <template #footer>
          <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="loadProfile">
            Tentar novamente
          </LaisPrimaryButton>
        </template>
      </LaisCard>

      <div v-else class="grid gap-5">
        <LaisCard
          v-if="!persisted"
          title="Complete o perfil da imobiliária"
          prefix-icon="i-lucide-info"
        >
          <p class="text-[13px] leading-6 text-lais-text-muted">
            Estamos usando os dados da conta como ponto de partida. Revise as
            informações e salve para criar o perfil oficial da imobiliária.
          </p>
        </LaisCard>

        <div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div class="grid gap-5">
            <LaisCard title="Identidade">
              <div class="grid gap-4 md:grid-cols-2">
                <LaisField
                  label="Nome fantasia"
                  :error="fieldError('nome_fantasia')"
                  required
                >
                  <input
                    v-model="form.nome_fantasia"
                    type="text"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField
                  label="Razão social"
                  :error="fieldError('razao_social')"
                >
                  <input
                    v-model="form.razao_social"
                    type="text"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="CNPJ" :error="fieldError('cnpj')">
                  <input
                    v-model="form.cnpj"
                    type="text"
                    inputmode="numeric"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <div class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_96px]">
                  <LaisField label="CRECI" :error="fieldError('creci')">
                    <input
                      v-model="form.creci"
                      type="text"
                      :class="inputClass"
                      :disabled="readOnly"
                    />
                  </LaisField>
                  <LaisField label="UF" :error="fieldError('creci_uf')">
                    <select
                      v-model="form.creci_uf"
                      :class="inputClass"
                      :disabled="readOnly"
                    >
                      <option value="">-</option>
                      <option v-for="uf in UFS" :key="uf" :value="uf">
                        {{ uf }}
                      </option>
                    </select>
                  </LaisField>
                </div>
              </div>
            </LaisCard>

            <LaisCard title="Contato e site">
              <div class="grid gap-4 md:grid-cols-2">
                <LaisField
                  label="Site da imobiliária"
                  :error="fieldError('site_url')"
                >
                  <input
                    v-model="form.site_url"
                    type="url"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <div
                  class="flex items-center gap-4 rounded-lais border border-lais-border-soft bg-white px-4 py-3 md:col-span-2"
                >
                  <Avatar
                    :src="logoImageUrl"
                    :name="form.nome_fantasia || 'Imobiliária'"
                    :size="72"
                    :allow-upload="!readOnly"
                    @upload="onLogoUpload"
                    @delete="onLogoDelete"
                  />
                  <div class="min-w-0">
                    <p class="text-[13px] font-semibold text-lais-text">
                      Logo da imobiliária
                    </p>
                    <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
                      Envie PNG, JPG, GIF ou WEBP até 15 MB. A URL externa
                      abaixo continua como fallback.
                    </p>
                    <p
                      v-if="fieldError('logo')"
                      class="mt-1 text-[12px] leading-5 text-lais-danger"
                    >
                      {{ fieldError('logo') }}
                    </p>
                  </div>
                </div>
                <LaisField
                  label="Logo URL externa (fallback)"
                  :error="fieldError('logo_url')"
                >
                  <input
                    v-model="form.logo_url"
                    type="url"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="Telefone" :error="fieldError('telefone')">
                  <input
                    v-model="form.telefone"
                    type="tel"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField
                  label="E-mail de contato"
                  :error="fieldError('email_contato')"
                >
                  <input
                    v-model="form.email_contato"
                    type="email"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
              </div>

              <div class="mt-5 border-t border-lais-border-soft pt-5">
                <h3 class="mb-3 text-[13px] font-semibold text-lais-text">
                  Endereço
                </h3>
                <div class="grid gap-4 md:grid-cols-2">
                  <LaisField label="Logradouro">
                    <input
                      v-model="form.endereco.logradouro"
                      type="text"
                      :class="inputClass"
                      :disabled="readOnly"
                    />
                  </LaisField>
                  <div class="grid gap-3 sm:grid-cols-[120px_minmax(0,1fr)]">
                    <LaisField label="Número">
                      <input
                        v-model="form.endereco.numero"
                        type="text"
                        :class="inputClass"
                        :disabled="readOnly"
                      />
                    </LaisField>
                    <LaisField label="Complemento">
                      <input
                        v-model="form.endereco.complemento"
                        type="text"
                        :class="inputClass"
                        :disabled="readOnly"
                      />
                    </LaisField>
                  </div>
                  <LaisField label="Bairro">
                    <input
                      v-model="form.endereco.bairro"
                      type="text"
                      :class="inputClass"
                      :disabled="readOnly"
                    />
                  </LaisField>
                  <LaisField label="Cidade">
                    <input
                      v-model="form.endereco.cidade"
                      type="text"
                      :class="inputClass"
                      :disabled="readOnly"
                    />
                  </LaisField>
                  <div class="grid gap-3 sm:grid-cols-[96px_minmax(0,1fr)]">
                    <LaisField label="UF" :error="fieldError('endereco.uf')">
                      <select
                        v-model="form.endereco.uf"
                        :class="inputClass"
                        :disabled="readOnly"
                      >
                        <option value="">-</option>
                        <option v-for="uf in UFS" :key="uf" :value="uf">
                          {{ uf }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField label="CEP" :error="fieldError('endereco.cep')">
                      <input
                        v-model="form.endereco.cep"
                        type="text"
                        inputmode="numeric"
                        :class="inputClass"
                        :disabled="readOnly"
                      />
                    </LaisField>
                  </div>
                </div>
              </div>
            </LaisCard>

            <LaisCard title="Marca e white-label">
              <div class="grid gap-4 md:grid-cols-3">
                <LaisField label="Cor primária">
                  <input
                    v-model="form.branding.primary_color"
                    type="text"
                    placeholder="#49317a"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="Cor secundária">
                  <input
                    v-model="form.branding.secondary_color"
                    type="text"
                    placeholder="#28E4A8"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="Cor de destaque">
                  <input
                    v-model="form.branding.accent_color"
                    type="text"
                    placeholder="#e9b76f"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
              </div>
              <div class="mt-4 grid gap-3 md:grid-cols-2">
                <LaisToggle
                  v-model="form.white_label_enabled"
                  label="White-label ativo"
                  helper="Permite que superfícies compatíveis usem a marca da imobiliária."
                  :disabled="readOnly"
                />
                <LaisToggle
                  v-model="form.branding.dark_mode_default"
                  label="Modo escuro padrão"
                  helper="Preferência de marca para consumidores que suportam tema escuro."
                  :disabled="readOnly"
                />
              </div>
            </LaisCard>

            <LaisCard title="Origens padrão">
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label
                  v-for="origin in originOptions"
                  :key="origin.value"
                  class="flex items-center gap-3 rounded-lais border p-3 transition-colors"
                  :class="
                    form.origens_default.includes(origin.value)
                      ? 'border-lais-primary bg-lais-primary-50/50'
                      : 'border-lais-border bg-white'
                  "
                >
                  <input
                    v-model="form.origens_default"
                    type="checkbox"
                    :value="origin.value"
                    :disabled="readOnly"
                    class="size-4 shrink-0 accent-[#49317a]"
                  />
                  <span
                    class="size-2.5 shrink-0 rounded-full"
                    :style="{ backgroundColor: origin.color }"
                  />
                  <span class="text-[13px] font-semibold text-lais-text">
                    {{ origin.label }}
                  </span>
                </label>
              </div>
              <template #footer>
                <LaisToggle
                  v-model="form.apply_to_account_origins"
                  label="Aplicar às origens da conta ao salvar"
                  helper="Atualiza também a configuração operacional usada pela Minha Lais."
                  :disabled="readOnly"
                />
              </template>
            </LaisCard>

            <LaisCard title="Filiais">
              <div class="grid gap-3 md:grid-cols-3">
                <LaisField label="Nome">
                  <input
                    v-model="branchDraft.name"
                    type="text"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="Código">
                  <input
                    v-model="branchDraft.code"
                    type="text"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="Tipo">
                  <select
                    v-model="branchDraft.branch_kind"
                    :class="inputClass"
                    :disabled="readOnly"
                  >
                    <option value="headquarters">Matriz</option>
                    <option value="branch">Filial</option>
                    <option value="virtual">Virtual</option>
                  </select>
                </LaisField>
                <LaisField label="Bairro">
                  <input
                    v-model="branchDraft.address.bairro"
                    type="text"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="Cidade">
                  <input
                    v-model="branchDraft.address.cidade"
                    type="text"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="UF">
                  <select
                    v-model="branchDraft.address.uf"
                    :class="inputClass"
                    :disabled="readOnly"
                  >
                    <option value="">-</option>
                    <option v-for="uf in UFS" :key="uf" :value="uf">
                      {{ uf }}
                    </option>
                  </select>
                </LaisField>
              </div>
              <template #footer>
                <div class="flex flex-col gap-3">
                  <LaisPrimaryButton
                    icon="i-lucide-building-2"
                    :is-loading="isBranchSaving"
                    :disabled="readOnly"
                    @click="saveBranch"
                  >
                    Salvar filial
                  </LaisPrimaryButton>
                  <div class="grid gap-2">
                    <div
                      v-for="branch in branches"
                      :key="branch.id"
                      class="rounded-lais border border-lais-border bg-white p-3 text-[13px]"
                    >
                      <div class="flex items-start justify-between gap-3">
                        <div>
                          <p class="font-semibold text-lais-text">
                            {{ branch.name }}
                          </p>
                          <p class="text-lais-text-muted">
                            {{ branch.code }} ·
                            {{ branch.address?.bairro || 'Sem bairro' }}
                          </p>
                        </div>
                        <span
                          class="rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                          :class="
                            branch.license_gate?.blocked
                              ? 'bg-rose-50 text-rose-700 ring-rose-200'
                              : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                          "
                        >
                          {{
                            branch.license_gate?.blocked
                              ? 'Licença bloqueia'
                              : 'Operável'
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </LaisCard>

            <LaisCard title="CRECI e licenças">
              <div class="grid gap-3 md:grid-cols-4">
                <LaisField label="Escopo">
                  <select
                    v-model="licenseDraft.holder_kind"
                    :class="inputClass"
                    :disabled="readOnly"
                  >
                    <option value="agency">Imobiliária</option>
                    <option value="branch">Filial</option>
                  </select>
                </LaisField>
                <LaisField label="Filial">
                  <select
                    v-model="licenseDraft.agency_branch_id"
                    :class="inputClass"
                    :disabled="
                      readOnly || licenseDraft.holder_kind !== 'branch'
                    "
                  >
                    <option value="">-</option>
                    <option
                      v-for="branch in branchOptions"
                      :key="branch.id"
                      :value="branch.id"
                    >
                      {{ branch.label }}
                    </option>
                  </select>
                </LaisField>
                <LaisField label="Número">
                  <input
                    v-model="licenseDraft.number"
                    type="text"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="UF">
                  <select
                    v-model="licenseDraft.uf"
                    :class="inputClass"
                    :disabled="readOnly"
                  >
                    <option value="">-</option>
                    <option v-for="uf in UFS" :key="uf" :value="uf">
                      {{ uf }}
                    </option>
                  </select>
                </LaisField>
                <LaisField label="Status">
                  <select
                    v-model="licenseDraft.status"
                    :class="inputClass"
                    :disabled="readOnly"
                  >
                    <option value="active">Ativa</option>
                    <option value="pending">Pendente</option>
                    <option value="expired">Vencida</option>
                    <option value="suspended">Suspensa</option>
                  </select>
                </LaisField>
                <LaisField label="Válida até">
                  <input
                    v-model="licenseDraft.valid_until"
                    type="date"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
              </div>
              <template #footer>
                <div class="flex flex-col gap-3">
                  <LaisPrimaryButton
                    icon="i-lucide-badge-check"
                    :is-loading="isLicenseSaving"
                    :disabled="readOnly"
                    @click="saveLicense"
                  >
                    Salvar licença
                  </LaisPrimaryButton>
                  <div class="grid gap-2">
                    <div
                      v-for="license in licenses"
                      :key="license.id"
                      class="rounded-lais border border-lais-border bg-white p-3 text-[13px]"
                    >
                      <div class="flex items-start justify-between gap-3">
                        <div>
                          <p class="font-semibold text-lais-text">
                            {{ license.holder_label }} · {{ license.number }}
                          </p>
                          <p class="text-lais-text-muted">
                            {{ license.license_kind }} /
                            {{ license.uf || '-' }} · vence
                            {{ formatDate(license.valid_until) }}
                          </p>
                        </div>
                        <span
                          class="rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                          :class="licenseStatusClass(license)"
                        >
                          {{ licenseStatusLabel(license) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </LaisCard>

            <LaisCard title="Territórios">
              <div class="grid gap-3 md:grid-cols-3">
                <LaisField label="Nome">
                  <input
                    v-model="territoryDraft.name"
                    type="text"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
                <LaisField label="Modalidade">
                  <select
                    v-model="territoryDraft.modality"
                    :class="inputClass"
                    :disabled="readOnly"
                  >
                    <option value="sale">Venda</option>
                    <option value="rental">Aluguel</option>
                    <option value="capture">Captação</option>
                    <option value="support">Suporte</option>
                    <option value="any">Todas</option>
                  </select>
                </LaisField>
                <LaisField label="Filial">
                  <select
                    v-model="territoryDraft.agency_branch_id"
                    :class="inputClass"
                    :disabled="readOnly"
                  >
                    <option value="">Matriz/conta</option>
                    <option
                      v-for="branch in branchOptions"
                      :key="branch.id"
                      :value="branch.id"
                    >
                      {{ branch.label }}
                    </option>
                  </select>
                </LaisField>
                <LaisField label="Cobertura">
                  <select
                    v-model="territoryDraft.coverage_kind"
                    :class="inputClass"
                    :disabled="readOnly"
                  >
                    <option value="city">Cidade</option>
                    <option value="neighborhood">Bairros</option>
                    <option value="uf">UF</option>
                  </select>
                </LaisField>
                <LaisField label="UF">
                  <select
                    v-model="territoryDraft.coverage_uf"
                    :class="inputClass"
                    :disabled="readOnly"
                  >
                    <option value="">-</option>
                    <option v-for="uf in UFS" :key="uf" :value="uf">
                      {{ uf }}
                    </option>
                  </select>
                </LaisField>
                <LaisField label="Cidade">
                  <input
                    v-model="territoryDraft.coverage_city"
                    type="text"
                    :class="inputClass"
                    :disabled="
                      readOnly || territoryDraft.coverage_kind === 'uf'
                    "
                  />
                </LaisField>
                <LaisField
                  v-if="territoryDraft.coverage_kind === 'neighborhood'"
                  label="Bairros"
                >
                  <input
                    v-model="territoryDraft.coverage_neighborhoods"
                    type="text"
                    placeholder="Ipanema, Copacabana"
                    :class="inputClass"
                    :disabled="readOnly"
                  />
                </LaisField>
              </div>
              <template #footer>
                <div class="flex flex-col gap-3">
                  <LaisPrimaryButton
                    icon="i-lucide-map-pinned"
                    :is-loading="isTerritorySaving"
                    :disabled="readOnly"
                    @click="saveTerritory"
                  >
                    Publicar território
                  </LaisPrimaryButton>
                  <div class="grid gap-2">
                    <div
                      v-for="territory in territories"
                      :key="territory.id"
                      class="rounded-lais border border-lais-border bg-white p-3 text-[13px]"
                    >
                      <p class="font-semibold text-lais-text">
                        {{ territory.name }}
                      </p>
                      <p class="text-lais-text-muted">
                        {{ territory.modality }} ·
                        {{
                          territory.current_version?.coverage_kind ||
                          'sem regra'
                        }}
                        · filial {{ territory.agency_branch_id || 'conta' }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </LaisCard>
          </div>

          <aside class="grid content-start gap-5">
            <LaisCard title="Resumo">
              <div class="flex items-start gap-3">
                <img
                  v-if="logoImageUrl"
                  :src="logoImageUrl"
                  :alt="form.nome_fantasia"
                  class="size-14 rounded-lais border border-lais-border object-cover"
                />
                <div
                  v-else
                  class="flex size-14 shrink-0 items-center justify-center rounded-lais border border-lais-border bg-white text-[18px] font-bold text-lais-primary"
                >
                  {{ form.nome_fantasia.slice(0, 1).toUpperCase() || 'I' }}
                </div>
                <div class="min-w-0">
                  <p
                    class="break-words text-[16px] font-semibold text-lais-text"
                  >
                    {{ form.nome_fantasia || 'Imobiliária' }}
                  </p>
                  <p class="mt-1 break-all text-[12px] text-lais-text-muted">
                    {{
                      form.site_url ||
                      form.email_contato ||
                      'Contato não informado'
                    }}
                  </p>
                </div>
              </div>

              <div class="mt-5 grid gap-2">
                <span
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px] font-semibold text-lais-text"
                >
                  <span
                    class="size-2.5 rounded-full"
                    :style="{ backgroundColor: previewBranding.primary }"
                  />
                  Primária {{ previewBranding.primary }}
                </span>
                <span
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px] font-semibold text-lais-text"
                >
                  <span
                    class="size-2.5 rounded-full"
                    :style="{ backgroundColor: previewBranding.secondary }"
                  />
                  Secundária {{ previewBranding.secondary }}
                </span>
                <span
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px] font-semibold text-lais-text"
                >
                  <span
                    class="size-2.5 rounded-full"
                    :style="{ backgroundColor: previewBranding.accent }"
                  />
                  Destaque {{ previewBranding.accent }}
                </span>
              </div>
            </LaisCard>

            <LaisCard title="Estado">
              <dl class="grid gap-3 text-[13px]">
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Cadastro</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ persisted ? 'Persistido' : 'Default da conta' }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Edição</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ canUpdate ? 'Administrador' : 'Somente leitura' }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Origens</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ form.origens_default.length }}
                  </dd>
                </div>
              </dl>
            </LaisCard>

            <LaisCard title="Contexto ativo">
              <dl class="grid gap-3 text-[13px]">
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Conta</dt>
                  <dd class="text-right font-semibold text-lais-text">
                    {{ activeContext.account_name || 'Conta atual' }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Papel</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ activeContext.role || '-' }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Última troca</dt>
                  <dd class="font-semibold text-lais-text">
                    {{
                      activeContext.last_switch_event?.occurred_at
                        ? formatDate(
                            activeContext.last_switch_event.occurred_at
                          )
                        : 'Sem troca registrada'
                    }}
                  </dd>
                </div>
              </dl>
              <template v-if="switchableAccounts.length" #footer>
                <div class="grid gap-2">
                  <button
                    v-for="accountOption in switchableAccounts"
                    :key="accountOption.account_id"
                    type="button"
                    class="flex items-center justify-between gap-3 rounded-lais border border-lais-border bg-white px-3 py-2 text-left text-[13px] font-semibold text-lais-text transition hover:border-lais-primary"
                    @click="switchContext(accountOption)"
                  >
                    <span>{{ accountOption.agency_name }}</span>
                    <span class="i-lucide-arrow-right-left size-4" />
                  </button>
                </div>
              </template>
            </LaisCard>

            <LaisCard title="Impacto operacional">
              <dl class="grid gap-3 text-[13px]">
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Usuários</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ impactSummary.users_count || users.length }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Corretores</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ impactSummary.corretores_count || corretores.length }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Listings publicados</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ impactSummary.published_listings_count || 0 }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Vitrines públicas</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ impactSummary.public_storefronts_count || 0 }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-lais-text-muted">Ações bloqueadas</dt>
                  <dd
                    class="font-semibold"
                    :class="
                      blockedSensitiveActions.length
                        ? 'text-rose-700'
                        : 'text-emerald-700'
                    "
                  >
                    {{ blockedSensitiveActions.length }}
                  </dd>
                </div>
              </dl>
            </LaisCard>

            <LaisCard title="Origens selecionadas">
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="label in selectedOriginLabels"
                  :key="label"
                  class="rounded-full bg-white px-3 py-1 text-[12px] font-semibold text-lais-text ring-1 ring-lais-border"
                >
                  {{ label }}
                </span>
              </div>
            </LaisCard>

            <LaisCard
              v-if="validationErrors.length"
              title="Pendências de validação"
            >
              <ul class="grid gap-2 text-[13px] leading-5 text-rose-700">
                <li v-for="error in validationErrors" :key="error">
                  {{ error }}
                </li>
              </ul>
            </LaisCard>
          </aside>
        </div>
      </div>
    </div>
  </section>
</template>
